import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class ScaleToFitService {
  scale = new BehaviorSubject<number>(1);
  elementToScale!: HTMLElement;

  handleFit(parentElementType?: string): Promise<void> {
    if (this.elementToScale) {
      const parentType = parentElementType ? parentElementType : 'section';
      return this.fitToParent(this.elementToScale, parentType);
    }

    return Promise.reject('Error: no set value for element to scale');
  }

  private scaleAmountNeededToFit(
    el: HTMLElement,
    parentType: string
  ): Promise<number> {
    return new Promise(resolve => {
      this.awaitChildren(el)
        .then(children => {
          return this.awaitChildrenDims(children)
            .then(childDims => {
              const parentSize = {
                width: el.closest(parentType)?.clientWidth || 300,
                height: el.closest(parentType)?.clientHeight || 300,
              };
              resolve(
                Math.min(
                  parentSize.width / childDims.width,
                  parentSize.height / childDims.height
                )
              );
            })
            .catch(err => {
              console.log(err);
              resolve(1);
            });
        })
        .catch(err => {
          console.log(err);
          resolve(1);
        });
    });
  }

  private awaitChildren(el: Element): Promise<HTMLCollection> {
    const tries = 20;
    return new Promise((resolve, reject) => {
      let i = 0;
      const interval = setInterval(() => {
        if (el.children.length) {
          clearInterval(interval);
          resolve(el.children);
        }
        if (i++ === tries) {
          clearInterval(interval);
          reject('Error: scale to fit get children max tries reached!');
        }
      }, 10);
    });
  }

  private awaitChildrenDims(
    children: HTMLCollection
  ): Promise<{ width: number; height: number }> {
    const tries = 20;
    return new Promise((resolve, reject) => {
      let curTry = 0;
      const interval = setInterval(() => {
        let childrenHeight = 0;
        let childrenWidth = 0;
        for (let i = 0; i < children.length; i++) {
          const computedStyle = window.getComputedStyle(children[i]);
          childrenHeight +=
            children[i].clientHeight +
            parseInt(computedStyle.marginTop, 10) +
            parseInt(computedStyle.marginBottom, 10);
          const newWidth =
            children[i].clientWidth +
            parseInt(computedStyle.marginLeft, 10) +
            parseInt(computedStyle.marginRight, 10);
          childrenWidth = childrenWidth < newWidth ? newWidth : childrenWidth;
        }

        if (childrenWidth !== 0 && childrenHeight !== 0) {
          clearInterval(interval);
          resolve({ width: childrenWidth, height: childrenHeight });
        }
        if (curTry++ === tries) {
          clearInterval(interval);
          reject(
            'Error: scale to fit get children dimensions max tries reached!'
          );
        }
      }, 10);
    });
  }

  private fitToParent(element: HTMLElement, parentType: string): Promise<void> {
    return this.scaleAmountNeededToFit(element, parentType).then(scale => {
      element.style.transformOrigin = '0 0';
      element.style.transform = `scale(${scale})`;
      if (element.parentElement) {
        element.parentElement.style.width =
          element.getBoundingClientRect().width + 'px';
      }
      this.scale.next(scale);
    });
  }
}
